import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import styles from "./index.module.scss";
import search from "../../../assets/images/search.svg";
import clearIcon from "../../../assets/images/close-icon.svg";
import opendropdown from "../../../assets/images/opendropdown.svg";
import closedropdown from "../../../assets/images/closedropdown.svg";
import filter from "../../../assets/images/filter.svg";
import appliedFilter from "../../../assets/images/filterApplied.svg";
import { forwardRef } from "react";
import { SidebarItemState, ToolBarProps } from "../../../types/seatApproval";
import sortIcon from "../../../assets/images/sort-icon.svg";
import arrowUp from "../../../assets/images/arrow-up.svg";
import arrowDown from "../../../assets/images/down-arrow.svg";
import { colorizeMahatriaInfinitheism } from "../ColorizeMahatriaInfinitheism";
import { dropDownnJSON, sortOptions } from "../../../constants/textConstants";

const PreferenceDropDown = forwardRef<HTMLDivElement, ToolBarProps>(
  (
    {
      selectedOption,
      isDropdownOpen,
      searchText,
      searchValue,
      sessions,
      setIsDropdownUserList,
      setSelectedOption,
      setDropdownAllocatedSessionId,
      setIsDropdownOpen,
      loadUsers,
      setSelectedSidebarItem,
      handleDropdownSessionSelect,
      setSearchText,
      setFinalSearchText,
      handleSearch,
      setSearchValue,
      setPaginationProps,
      isOpenFilter,
      setIsOpenFilter,
      isFilterApplied = false,
      setAppliedFilters,
      handleSort,
      setFilterItem,
      sortState,
      isSortDropdownOpen,
      setIsSortDropdownOpen,
    },
    ref,
  ) => {
    useEffect(() => {
      if (!isSortDropdownOpen) return;
      const handleClickOutside = (event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setIsSortDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isSortDropdownOpen]);

    const getArrowDirection = (optionValue: string) => {
      if (sortState.sortKey === optionValue) {
        // If this option is currently selected, use current sort order
        return sortState.sortOrder === "desc" ? arrowDown : arrowUp;
      } else {
        // If not selected, show the initial direction for each option
        return optionValue === "registrationDate" ? arrowDown : arrowUp;
      }
    };

    return (
      <div
        className={styles.toolbarContainer}
        ref={ref}
        onClick={() => {
          if (isDropdownOpen) setIsDropdownOpen(false);
          if (isSortDropdownOpen) setIsSortDropdownOpen(false);
        }}
      >
        <div
          className={styles.statusSelector}
          onClick={(e) => {
            e.stopPropagation();
            // Close sort dropdown when opening KPI dropdown
            if (isSortDropdownOpen) {
              setIsSortDropdownOpen(false);
            }
            setIsDropdownOpen((prev: boolean) => !prev);
          }}
        >
          <span className={styles.toolbar}>
            {colorizeMahatriaInfinitheism(selectedOption?.label || "Select")}{" "}
          </span>
          <span className={styles.dropdownArrow}>
            <img
              src={isDropdownOpen ? opendropdown : closedropdown}
              alt="dropdown"
            />
          </span>
          {isDropdownOpen && (
            <div
              className={styles.dropdownMenu}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`${styles.dropdownItem} ${selectedOption?.value === "all" ? styles.selected : ""}`}
                onClick={() => {
                  handleSort(false);
                  setPaginationProps((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                  setIsDropdownUserList(false);
                  setSelectedOption({
                    label: "All",
                    value: "all",
                    data: "",
                  });
                  setDropdownAllocatedSessionId(null);
                  setIsDropdownOpen(false);
                  setAppliedFilters({});
                  setSelectedSidebarItem({
                    key: "total",
                    kpiCategory: "all",
                    kpiFilter: "all",
                  });
                  setSearchText("");
                  setFinalSearchText("");
                  setSearchValue({ value: "", open: false });
                  loadUsers(
                    0,
                    true,
                    {},
                    {
                      kpiFilter: "all",
                      approvalStatus: "all",
                      sortKey: "",
                      sortOrder: "asc",
                    },
                  );
                  setFilterItem({
                    kpiFilter: "all",
                    approvalStatus: "all",
                  });
                }}
              >
                All
              </div>
              <div
                className={`${styles.dropdownItem} ${
                  selectedOption?.label === "Unassigned" ||
                  selectedOption?.value === "registered"
                    ? styles.selected
                    : ""
                }`}
                onClick={() => {
                  handleSort(false);
                  setIsDropdownUserList(false);
                  setSelectedOption({
                    label: "Unassigned",
                    value: "registered",
                    data: "",
                  });
                  setPaginationProps((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                  setAppliedFilters({});
                  setSelectedSidebarItem({
                    key: "total_unallocated",
                    kpiCategory: "unallocated",
                    kpiFilter: "total_unallocated",
                  });
                  setDropdownAllocatedSessionId(null);
                  setIsDropdownOpen(false);
                  setSearchText("");
                  setSearchValue({ value: "", open: false });
                  setFinalSearchText("");
                  loadUsers(
                    0,
                    true,
                    {},
                    {
                      kpiFilter: "total_unallocated",
                      approvalStatus: "unallocated",
                      sortKey: "",
                      sortOrder: "asc",
                    },
                  );
                  setFilterItem("total_unallocated");
                }}
              >
                Unassigned
              </div>
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={`${styles.dropdownItem} ${
                    (selectedOption?.value === session.type &&
                      selectedOption?.data?.id === session.id) ||
                    selectedOption?.label === session.name
                      ? styles.selected
                      : ""
                  }`}
                  onClick={() => {
                    handleSort(false);
                    if (session.name === "Hold" || session.name === "YTD") {
                      setSelectedSidebarItem({
                        key: `${session.type}_total`,
                        kpiCategory: session.kpiCategory,
                        kpiFilter: session.kpiFilter,
                      });
                      setDropdownAllocatedSessionId(null);
                    } else {
                      setDropdownAllocatedSessionId(session.id);
                      setSelectedSidebarItem({
                        key: `${session.kpiFilter}_total`,
                        kpiCategory: session.kpiCategory,
                        kpiFilter: session.kpiFilter,
                      });
                    }
                    setSearchText("");
                    setFinalSearchText("");
                    setSearchValue({ value: "", open: false });
                    loadUsers(
                      0,
                      true,
                      {},
                      {
                        kpiFilter: session.kpiFilter,
                        approvalStatus: session.kpiCategory,
                        sortKey: "",
                        sortOrder: "asc",
                      },
                    );
                    setFilterItem({
                      kpiFilter: session.kpiFilter,
                      approvalStatus: session.kpiCategory,
                    });
                    setPaginationProps((prev) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                    setIsDropdownUserList(false);
                    setAppliedFilters({});
                    setSelectedOption({
                      label: session.name,
                      value: session.type,
                      data: session,
                    });

                    setIsDropdownOpen(false);
                  }}
                >
                  <span>{colorizeMahatriaInfinitheism(session.name)}</span>
                </div>
              ))}
              <div className={styles.dropdownDivider} />
              <div
                className={`${styles.dropdownItem} ${
                  selectedOption?.label === "Swap" ||
                  selectedOption?.value === "swap requests"
                    ? styles.selected
                    : ""
                }`}
                onClick={() => {
                  handleSort(false);
                  setSelectedSidebarItem({
                    key: "swap_total",
                    kpiCategory: "all",
                    kpiFilter: "swap-requests",
                  });
                  setPaginationProps((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                  setDropdownAllocatedSessionId(null);
                  setIsDropdownOpen(false);
                  setIsDropdownUserList(false);
                  setSelectedOption({
                    label: "Swap",
                    value: "swap requests",
                    data: "",
                  });
                  setSearchText("");
                  setFinalSearchText("");
                  setSearchValue({ value: "", open: false });
                  loadUsers(
                    0,
                    true,
                    {},
                    {
                      kpiFilter: "swap-requests",
                      approvalStatus: "all",
                      swapRequests: "wants_swap",
                      sortKey: "",
                      sortOrder: "asc",
                    },
                  );
                  setFilterItem({
                    kpiFilter: "swap-requests",
                    approvalStatus: "all",
                    swapRequests: "wants_swap",
                  });
                }}
              >
                Swap requests
              </div>
              <div
                className={`${styles.dropdownItem} ${
                  selectedOption?.label === dropDownnJSON.CANCELLED.label ||
                  selectedOption?.value === dropDownnJSON.CANCELLED.value
                    ? styles.selected
                    : ""
                }`}
                onClick={() => {
                  handleSort(false);
                  setSelectedSidebarItem({
                    key: "cancelled_total",
                    kpiCategory: dropDownnJSON.CANCELLED.value,
                    kpiFilter: dropDownnJSON.CANCELLED.value,
                  });
                  setPaginationProps((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                  setDropdownAllocatedSessionId(null);
                  setIsDropdownOpen(false);
                  setIsDropdownUserList(false);
                  setSelectedOption({
                    label:  dropDownnJSON.CANCELLED.label,
                    value: dropDownnJSON.CANCELLED.value,
                    data: "",
                  });
                  setSearchText("");
                  setFinalSearchText("");
                  setSearchValue({ value: "", open: false });
                  loadUsers(
                    0,
                    true,
                    {},
                    {
                      kpiFilter:dropDownnJSON.CANCELLED.value,
                      approvalStatus: dropDownnJSON.CANCELLED.value,
                    },
                  );
                  setFilterItem({
                    kpiFilter: dropDownnJSON.CANCELLED.value,
                    approvalStatus: dropDownnJSON.CANCELLED.value,
                  });
                }}
              >
             { dropDownnJSON.CANCELLED.label}
              </div>
            </div>
          )}
        </div>
        <div className={styles.searchContainer}>
          {searchValue.open && (
            <div className={styles.search} data-testid="search-container">
              <input
                type="text"
                className={
                  searchText ? styles.searchInputWithText : styles.searchInput
                }
                autoFocus
                value={searchText}
                placeholder="Search seeker with name or mobile number"
                onChange={(e) => {
                  setSearchText(e.target.value);
                  if (e.target.value === "") {
                    handleSearch("");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(e.currentTarget.value);
                    setPaginationProps((prev: any) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                  }
                }}
                data-testid="search-input"
              />
              {searchText && (
                <button
                  className={styles.clearIcon}
                  onClick={() => {
                    setSearchText("");
                    setFinalSearchText("");
                    setPaginationProps((prev: any) => ({
                      ...prev,
                      currentPage: 1,
                    }));
                  }}
                  data-testid="clear-button"
                >
                  <img src={clearIcon} alt="clear" data-testid="clear-icon" />
                </button>
              )}
            </div>
          )}
          <Tooltip title="Search" arrow>
            <button className={styles.searchIcon} data-testid="search-button">
              <img
                src={search}
                alt="search"
                data-testid="search-icon"
                onClick={() =>
                  setSearchValue({ value: "", open: !searchValue.open })
                }
              />
            </button>
          </Tooltip>
          <Tooltip title="Filter" arrow>
            <img
              src={isFilterApplied ? appliedFilter : filter}
              alt="toggle dropdown"
              className={styles.filterIcon}
              onClick={() => {
                setIsOpenFilter(!isOpenFilter);
              }}
            />
          </Tooltip>

          <div
            className={styles.sortContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title="Sort" arrow>
              <>
                <img
                  src={sortIcon}
                  alt="sort icon"
                  className={styles.filterIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Close KPI dropdown when opening sort dropdown
                    if (isDropdownOpen) {
                      setIsDropdownOpen(false);
                    }
                    setIsSortDropdownOpen((prev) => !prev);
                  }}
                />
                {sortState.sortKey != "" && (
                  <sup className={styles.blueDot}></sup>
                )}
              </>
            </Tooltip>
            {isSortDropdownOpen && (
              <div className={styles.dropdownMenu}>
                {sortOptions.map((option) => (
                  <div
                    key={option.value}
                    className={styles.dropdownItem}
                    onClick={() => handleSort(option.value)}
                  >
                    <span className={styles.optionName}>{option.label}</span>
                    <img
                  src={getArrowDirection(option.value)}
                  alt="sort"
                  className={`${styles.sortArrow} ${
                    sortState.sortKey === option.value ? styles.visible : ""
                  }`}
                />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

PreferenceDropDown.displayName = "PreferenceDropDown";
export { PreferenceDropDown };
